import * as React from 'react';

import { LegalEntityApiError } from '@/api/legal-entity-client';
import { useLegalEntityHierarchy } from '@/api/use-legal-entities';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import { establishmentStateLabel } from '@/lib/supplier-labels';

// =============================================================================
// legal-entity-hierarchy-screen.tsx — Écran « Hiérarchie multi-établissement » (T7-MULTI-ETAB).
// =============================================================================
// Vue GROUPÉE par SIREN (personne morale) de ses établissements (SIRET). GET
// /admin/legal-entities/:siren/establishments (read-model cross-fournisseur withAdmin, plan ARCH §1.1).
//
// LECTURE SEULE (décision GATE_HUMAN T7 = vue + navigation, AUCUN write — plan ARCH §6.1,
// invariant SIRET = SIREN + NIC) : aucun bouton d'écriture, aucune mutation. La navigation
// = par ligne, ouverture de la fiche A-04 du SIRET (onOpen) ; retour vers le registre (onBack).
//
// Le composant reste testable HORS ROUTEUR (props contrôlées : siren + callbacks de navigation).
//
// WCAG 2.1 AA (ST-NFR-03) : <table> avec <caption> + <th scope> ; états rendus en LIBELLÉ TEXTE
// (le badge n'est qu'un renfort visuel, jamais la seule couleur) ; messages role="status"/role="alert" ;
// bouton retour et actions au clavier natif ; aria-label explicite par action de ligne.

export interface LegalEntityHierarchyScreenProps {
  /** SIREN de la personne morale (ressource d'URL, portée par l'adaptateur routeur). */
  readonly siren: string;
  /** Ouvre la fiche A-04 d'un SIRET (navigation portée par l'adaptateur routeur). */
  readonly onOpen: (siret: string) => void;
  /** Retour vers le registre fournisseurs (navigation portée par l'adaptateur routeur). */
  readonly onBack: () => void;
}

/** Formate une date d'enrôlement ISO 8601 en date FR ; « — » si `null` ; brut si ISO invalide. */
function formatDate(iso: string | null): string {
  if (iso === null) {
    return '—';
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

/** Message utilisateur mappé PAR CODE HTTP (404 = SIREN sans établissement ; 400 = SIREN invalide). */
function hierarchyErrorMessage(error: Error): string {
  if (error instanceof LegalEntityApiError) {
    if (error.status === 404) {
      return "Aucun établissement n'est référencé pour ce SIREN.";
    }
    if (error.status === 400) {
      return 'SIREN invalide (9 chiffres attendus).';
    }
  }
  return `Erreur de chargement : ${error.message}`;
}

export function LegalEntityHierarchyScreen({
  siren,
  onOpen,
  onBack,
}: LegalEntityHierarchyScreenProps): React.JSX.Element {
  const hierarchy = useLegalEntityHierarchy(siren);

  return (
    <div className="space-y-8">
      <header className="space-y-4">
        <Button type="button" variant="outline" size="sm" onClick={onBack}>
          Retour au référentiel
        </Button>
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">Personne morale</p>
          <h1 className="text-2xl font-semibold tracking-tight">
            {hierarchy.data?.raisonSocialeLegale ?? 'Hiérarchie multi-établissement'}
          </h1>
          <p className="font-mono text-muted-foreground">SIREN {siren}</p>
        </div>
      </header>

      <HierarchyBody hierarchy={hierarchy} onOpen={onOpen} />
    </div>
  );
}

function HierarchyBody({
  hierarchy,
  onOpen,
}: {
  readonly hierarchy: ReturnType<typeof useLegalEntityHierarchy>;
  readonly onOpen: (siret: string) => void;
}): React.JSX.Element {
  if (hierarchy.isPending) {
    return (
      <p role="status" className="text-muted-foreground">
        Chargement de la hiérarchie…
      </p>
    );
  }
  if (hierarchy.isError) {
    return (
      <p role="alert" className="text-destructive">
        {hierarchyErrorMessage(hierarchy.error)}
      </p>
    );
  }

  const { etablissements } = hierarchy.data;
  if (etablissements.length === 0) {
    return (
      <p role="status" className="text-muted-foreground">
        Aucun établissement rattaché à ce SIREN.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <p role="status" className="text-sm text-muted-foreground">
        {etablissements.length} établissement{etablissements.length > 1 ? 's' : ''}{' '}
        rattaché{etablissements.length > 1 ? 's' : ''} à ce SIREN.
      </p>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="ds-table w-full border-collapse text-sm">
          <caption className="sr-only">
            Établissements rattachés au SIREN : numéro SIRET, état de l'établissement et
            date d'enrôlement. Chaque ligne ouvre la fiche du fournisseur correspondant.
          </caption>
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-muted-foreground">
              <th scope="col" className="px-4 py-3 font-medium">
                SIRET
              </th>
              <th scope="col" className="px-4 py-3 font-medium">
                État
              </th>
              <th scope="col" className="px-4 py-3 font-medium">
                Date d'enrôlement
              </th>
              <th scope="col" className="px-4 py-3 font-medium">
                <span className="sr-only">Action</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {etablissements.map((etab) => {
              const state = establishmentStateLabel(etab.etablissementState);
              return (
                <tr
                  key={etab.siret}
                  className="border-b border-border last:border-b-0"
                >
                  <td className="px-4 py-3 align-top font-mono">{etab.siret}</td>
                  <td className="px-4 py-3 align-top">
                    {/* Badge = renfort ; le LIBELLÉ texte porte l'information (WCAG 2.1 AA). */}
                    <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
                  </td>
                  <td className="px-4 py-3 align-top whitespace-nowrap text-muted-foreground">
                    {formatDate(etab.enrolledAt)}
                  </td>
                  <td className="px-4 py-3 align-top text-right">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => onOpen(etab.siret)}
                      aria-label={`Ouvrir la fiche du fournisseur ${etab.siret}`}
                    >
                      Ouvrir
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
